/** Main-thread wrapper around the pipeline worker. */
import type { FromWorker, RenderStats, ToWorker } from '../shared/messages';
import type { Params } from '../shared/params';
import type { Stage } from '../shared/stages';

export interface ClientHandlers {
  ready: (info: { renderer: string; maxTextureSize: number; hasFloatBlend: boolean }) => void;
  unsupported: (message: string) => void;
  source: (bitmap: ImageBitmap, width: number, height: number, id: string) => void;
  frame: (bitmap: ImageBitmap, stats: RenderStats) => void;
  progress: (fraction: number, label: string) => void;
  exported: (blob: Blob, width: number, height: number) => void;
  error: (message: string) => void;
}

export class PipelineClient {
  private worker: Worker;
  private handlers: Partial<ClientHandlers> = {};
  private seq = 0;

  constructor() {
    this.worker = new Worker(new URL('../worker/pipeline.worker.ts', import.meta.url), {
      type: 'module',
      name: 'pixeloe-pipeline',
    });
    this.worker.onmessage = (e: MessageEvent<FromWorker>) => this.dispatch(e.data);
    this.worker.onerror = (e) => this.handlers.error?.(e.message || 'worker crashed');
  }

  on<K extends keyof ClientHandlers>(key: K, fn: ClientHandlers[K]): this {
    this.handlers[key] = fn;
    return this;
  }

  private dispatch(msg: FromWorker): void {
    switch (msg.type) {
      case 'ready':
        this.handlers.ready?.(msg);
        break;
      case 'unsupported':
        this.handlers.unsupported?.(msg.message);
        break;
      case 'source':
        this.handlers.source?.(msg.bitmap, msg.width, msg.height, msg.id);
        break;
      case 'frame':
        this.handlers.frame?.(msg.bitmap, msg.stats);
        break;
      case 'progress':
        this.handlers.progress?.(msg.fraction, msg.label);
        break;
      case 'exported':
        this.handlers.exported?.(msg.blob, msg.width, msg.height);
        break;
      case 'error':
        this.handlers.error?.(msg.message);
        break;
    }
  }

  private send(msg: ToWorker, transfer: Transferable[] = []): void {
    this.worker.postMessage(msg, transfer);
  }

  /** Hand the worker its own OffscreenCanvas and initialise WebGL. */
  init(): void {
    const canvas = document.createElement('canvas');
    const offscreen = canvas.transferControlToOffscreen();
    this.send({ type: 'init', canvas: offscreen }, [offscreen]);
  }

  load(blob: Blob, id: string): void {
    this.send({ type: 'load', blob, id });
  }

  render(params: Params, interactive: boolean): void {
    this.seq++;
    this.send({ type: 'render', params, interactive });
  }

  exportImage(params: Params, format: 'image/png' | 'image/webp', stage?: Stage): void {
    this.send({ type: 'export', params, format, stage });
  }

  /**
   * Queue an export and resolve with the encoded blob. Used by "Copy" and by
   * the export buttons when the caller wants the bytes rather than a download.
   */
  exportBlob(
    params: Params,
    format: 'image/png' | 'image/webp',
    stage?: Stage,
  ): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      const prevExported = this.handlers.exported;
      const prevError = this.handlers.error;
      const restore = () => {
        this.handlers.exported = prevExported;
        this.handlers.error = prevError;
      };
      this.handlers.exported = (blob, width, height) => {
        restore();
        prevExported?.(blob, width, height);
        resolve(blob);
      };
      this.handlers.error = (message) => {
        restore();
        prevError?.(message);
        reject(new Error(message));
      };
      this.send({ type: 'export', params, format, stage });
    });
  }

  dispose(): void {
    this.worker.terminate();
  }
}
